import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createSource, getAuthors, Author } from '../lib/api';

interface SourceFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const SourceForm: React.FC<SourceFormProps> = ({ onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const [authors, setAuthors] = useState<Author[]>([]);
  const [isLoadingAuthors, setIsLoadingAuthors] = useState(true);
  
  const [type, setType] = useState<'book' | 'article'>('book');
  const [title, setTitle] = useState('');
  const [isbn, setIsbn] = useState('');
  const [doi, setDoi] = useState('');
  const [issn, setIssn] = useState('');
  const [publisher, setPublisher] = useState('');
  const [year, setYear] = useState('');
  const [selectedAuthorIds, setSelectedAuthorIds] = useState<number[]>([]);
  const [authorSearch, setAuthorSearch] = useState('');
  const [showAuthorDropdown, setShowAuthorDropdown] = useState(false);
  const authorSearchRef = useRef<HTMLInputElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Autoren laden
  useEffect(() => {
    const loadAuthors = async () => {
      try {
        const response = await getAuthors();
        setAuthors(response.data);
      } catch (err) {
        console.error('Fehler beim Laden der Autoren:', err);
      } finally {
        setIsLoadingAuthors(false);
      }
    };
    loadAuthors();
  }, []);

  const handleAuthorToggle = (authorId: number) => {
    setSelectedAuthorIds((prev) =>
      prev.includes(authorId)
        ? prev.filter((id) => id !== authorId)
        : [...prev, authorId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('Titel ist erforderlich');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const sourceData = {
      type,
      title: title.trim(),
      isbn: isbn.trim() || undefined,
      doi: doi.trim() || undefined,
      issn: issn.trim() || undefined,
      publisher: publisher.trim() || undefined,
      year: year ? parseInt(year) : undefined,
      author_ids: selectedAuthorIds,
    };

    try {
      await createSource(sourceData);
      onSuccess?.();
      navigate('/sources');
    } catch (err: unknown) {
      console.error('Fehler beim Erstellen:', err);
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      if (detail) {
        setError(detail);
      } else {
        setError('Fehler beim Speichern. Bitte versuche es erneut.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">
          Neue Quelle erstellen
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Typ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Typ
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="book"
                  checked={type === 'book'}
                  onChange={() => setType('book')}
                  className="mr-2"
                />
                📖 Buch
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="article"
                  checked={type === 'article'}
                  onChange={() => setType('article')}
                  className="mr-2"
                />
                📄 Artikel
              </label>
            </div>
          </div>

          {/* Titel */}
          <div>
            <label
              htmlFor="title"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Titel <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={type === 'book' ? 'z.B. Der Steppenwolf' : 'z.B. KI in der modernen Softwareentwicklung'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isSubmitting}
            />
          </div>

          {/* Autoren */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Autoren
            </label>
            {isLoadingAuthors ? (
              <p className="text-gray-500">Lade Autoren...</p>
            ) : authors.length === 0 ? (
              <p className="text-yellow-600">
                Noch keine Autoren vorhanden. Bitte erstelle zuerst einen Autor.
              </p>
            ) : (
              <div>
                {/* Selected author tags */}
                {selectedAuthorIds.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.5rem' }}>
                    {selectedAuthorIds.map((id) => {
                      const author = authors.find((a) => a.id === id);
                      if (!author) return null;
                      return (
                        <span key={id} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                          background: 'var(--primary)', color: 'white',
                          borderRadius: '999px', padding: '0.2rem 0.65rem',
                          fontSize: '0.8125rem', fontWeight: 500,
                        }}>
                          {author.name}
                          <button
                            type="button"
                            onClick={() => handleAuthorToggle(id)}
                            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: '1rem', opacity: 0.75 }}
                          >×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
                {/* Search input */}
                <div style={{ position: 'relative' }}>
                  <input
                    ref={authorSearchRef}
                    type="text"
                    value={authorSearch}
                    onChange={(e) => { setAuthorSearch(e.target.value); setShowAuthorDropdown(true); }}
                    onFocus={() => setShowAuthorDropdown(true)}
                    onBlur={() => setTimeout(() => setShowAuthorDropdown(false), 150)}
                    placeholder="Autor suchen…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isSubmitting}
                  />
                  {showAuthorDropdown && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                      background: 'white', border: '1px solid #d1d5db', borderRadius: '0.375rem',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '180px', overflowY: 'auto',
                      marginTop: '0.25rem',
                    }}>
                      {authors
                        .filter((a) =>
                          a.name.toLowerCase().includes(authorSearch.toLowerCase()) &&
                          !selectedAuthorIds.includes(a.id)
                        )
                        .map((author) => (
                          <div
                            key={author.id}
                            onMouseDown={() => { handleAuthorToggle(author.id); setAuthorSearch(''); }}
                            style={{ padding: '0.5rem 0.75rem', cursor: 'pointer' }}
                            className="hover:bg-gray-100"
                          >
                            {author.name}
                          </div>
                        ))}
                      {authors.filter((a) =>
                        a.name.toLowerCase().includes(authorSearch.toLowerCase()) &&
                        !selectedAuthorIds.includes(a.id)
                      ).length === 0 && (
                        <div style={{ padding: '0.5rem 0.75rem', color: '#9ca3af', fontSize: '0.875rem' }}>
                          Keine Ergebnisse
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Jahr */}
          <div>
            <label
              htmlFor="year"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Jahr
            </label>
            <input
              type="number"
              id="year"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="z.B. 1927"
              min="1000"
              max="2099"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isSubmitting}
            />
          </div>

          {/* Publisher */}
          <div>
            <label
              htmlFor="publisher"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Verlag
            </label>
            <input
              type="text"
              id="publisher"
              value={publisher}
              onChange={(e) => setPublisher(e.target.value)}
              placeholder="z.B. Suhrkamp Verlag"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isSubmitting}
            />
          </div>

          {/* ISBN (nur für Bücher) */}
          {type === 'book' && (
            <div>
              <label
                htmlFor="isbn"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                ISBN
              </label>
              <input
                type="text"
                id="isbn"
                value={isbn}
                onChange={(e) => setIsbn(e.target.value)}
                placeholder="z.B. 978-3-518-38090-7"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* DOI (nur für Artikel) */}
          {type === 'article' && (
            <div>
              <label
                htmlFor="doi"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                DOI
              </label>
              <input
                type="text"
                id="doi"
                value={doi}
                onChange={(e) => setDoi(e.target.value)}
                placeholder="z.B. 10.1000/xyz123"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* ISSN (nur für Artikel) */}
          {type === 'article' && (
            <div>
              <label
                htmlFor="issn"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                ISSN
              </label>
              <input
                type="text"
                id="issn"
                value={issn}
                onChange={(e) => setIssn(e.target.value)}
                placeholder="z.B. 1234-5678"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Fehleranzeige */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => onCancel ? onCancel() : navigate('/sources')}
              className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition-colors"
              disabled={isSubmitting}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !title.trim()}
              className="flex-1 bg-blue-900 text-white py-2 px-4 rounded-md hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Speichern...' : 'Quelle erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
